import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../styles/SearchBar.css';
import dishesData from '../assets/dishes2.json';

const SearchBar = () => {
  const [searchType, setSearchType] = useState('dishes'); // 'dishes' or 'users'
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const searchContainerRef = useRef(null);
  const searchInputRef = useRef(null);
  const navigate = useNavigate();

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Search function for dishes
  const searchDishes = (query) => {
    // If query is empty, show popular/recent dishes (limited number)
    if (!query.trim()) {
      // Return top 8 dishes as suggestions when no query is provided
      const topDishes = dishesData.slice(0, 8);
      setSuggestions(topDishes);
      return;
    }

    const filteredDishes = dishesData.filter(dish => {
      const searchLower = query.toLowerCase();
      return (
        dish.title.toLowerCase().includes(searchLower) ||
        dish.restaurantName.toLowerCase().includes(searchLower) ||
        (dish.tags && dish.tags.some(tag => tag.toLowerCase().includes(searchLower)))
      );
    }).slice(0, 5); // Limit to 5 suggestions

    setSuggestions(filteredDishes);
  };

  // Function to determine tag styling based on tag content
  const getTagStyle = (tag) => {
    const tagType = tag.toLowerCase().replace(/\s+/g, '-');
    
    // Default tag style
    let style = {
      backgroundColor: '#e0e0e0',
      color: '#333'
    };
    
    // Spice Level
    if (tagType === 'hot') {
      style = { backgroundColor: '#ffcdd2', color: '#c62828' };
    } else if (tagType === 'medium') {
      style = { backgroundColor: '#ffe0b2', color: '#ef6c00' };
    } else if (tagType === 'mild') {
      style = { backgroundColor: '#fff9c4', color: '#f57f17' };
    } else if (tagType === 'no-spice') {
      style = { backgroundColor: '#cfd8dc', color: '#455a64' };
    } 
    // Dietary - Vegetarian/Vegan
    else if (tagType === 'veggie' || tagType === 'vegetarian' || tagType === 'vegan') {
      style = { backgroundColor: '#c8e6c9', color: '#2e7d32' };
    } 
    // Dietary - Restrictions
    else if (tagType === 'gluten-free') {
      style = { backgroundColor: '#bbdefb', color: '#0d47a1' };
    } else if (tagType === 'soy-free') {
      style = { backgroundColor: '#e1bee7', color: '#6a1b9a' };
    } else if (tagType === 'dairy-free') {
      style = { backgroundColor: '#ffccbc', color: '#d84315' };
    } else if (tagType === 'nut-free') {
      style = { backgroundColor: '#d1c4e9', color: '#4527a0' };
    } 
    // Cuisine Types
    else if (tagType === 'mexican') {
      style = { backgroundColor: '#FFECB3', color: '#FF8F00' };
    } else if (tagType === 'chinese') {
      style = { backgroundColor: '#FFCDD2', color: '#B71C1C' };
    } else if (tagType === 'japanese') {
      style = { backgroundColor: '#F3E5F5', color: '#6A1B9A' };
    } else if (tagType === 'korean') {
      style = { backgroundColor: '#B2EBF2', color: '#00838F' };
    } else if (tagType === 'indian') {
      style = { backgroundColor: '#FFECB3', color: '#E65100' };
    } else if (tagType === 'american') {
      style = { backgroundColor: '#C5CAE9', color: '#283593' };
    } else if (tagType === 'italian') {
      style = { backgroundColor: '#DCEDC8', color: '#558B2F' };
    }
    // Allergens / Contains
    else if (tagType === 'contains-seafood') {
      style = { backgroundColor: '#b2dfdb', color: '#00695c' };
    }
    
    return style;
  };

  // Search function for users
  const searchUsers = async (query) => {
    if (!query.trim()) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      // Use the token if available for authenticated requests
      const token = sessionStorage.getItem("token");
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      // Fetch users that match the search query
      const response = await fetch(
        `${process.env.REACT_APP_API_PATH}/users?limit=5`,
        {
          method: 'GET',
          headers: headers
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const result = await response.json();
      let users = [];
      
      if (Array.isArray(result) && result.length > 0) {
        // Extract users array from response
        if (Array.isArray(result[0])) {
          users = result[0];
        } else {
          users = result;
        }
        
        // Filter users client-side based on username/email matching
        users = users.filter(user => {
          const searchLower = query.toLowerCase();
          const username = user.attributes?.username || '';
          const email = user.attributes?.email || '';
          
          return username.toLowerCase().includes(searchLower) || 
                 email.toLowerCase().includes(searchLower);
        }).slice(0, 5); // Limit to 5 suggestions
      }
      
      setSuggestions(users);
    } catch (error) {
      console.error('Error searching users:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle search input changes
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    if (searchType === 'dishes') {
      searchDishes(query);
    } else {
      searchUsers(query);
    }
  };

  // Handle search input focus
  const handleSearchFocus = () => {
    setShowSuggestions(true);
    // If search field is empty and type is 'dishes', show suggested dishes
    if (searchQuery.trim() === '' && searchType === 'dishes') {
      searchDishes('');
    }
  };

  // Navigate to the top result when Enter is pressed
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && suggestions.length > 0) {
      e.preventDefault();
      const topResult = suggestions[0];
      
      if (searchType === 'dishes') {
        navigate(`/dish/${topResult.id}`);
      } else {
        navigate(`/user/${topResult.id}`);
      }
      
      setShowSuggestions(false);
    }
  };

  // Handle search type toggle
  const handleSearchTypeToggle = () => {
    const newType = searchType === 'dishes' ? 'users' : 'dishes';
    setSearchType(newType);
    setSearchQuery('');
    setSuggestions([]);
    
    // If switching to dishes type with empty query, show dish suggestions
    if (newType === 'dishes' && searchInputRef.current === document.activeElement) {
      searchDishes('');
    }
  };

  return (
    <div className="search-container" ref={searchContainerRef}>
      <div className="search-bar-wrapper">
        <div className="search-input-container">
          <input
            ref={searchInputRef}
            type="text"
            className="search-input"
            placeholder={`Search ${searchType}...`}
            value={searchQuery}
            onChange={handleSearchChange}
            onFocus={handleSearchFocus}
            onKeyDown={handleKeyDown}
          />
          <div className="search-type-toggle">
            <div className={`toggle-option ${searchType === 'dishes' ? 'active' : ''}`}>
              Dishes
            </div>
            <label className="switch">
              <input 
                type="checkbox" 
                checked={searchType === 'users'} 
                onChange={handleSearchTypeToggle}
              />
              <span className="slider round"></span>
            </label>
            <div className={`toggle-option ${searchType === 'users' ? 'active' : ''}`}>
              Users
            </div>
          </div>
        </div>
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <div className="suggestions-container">
          {isLoading ? (
            <div className="suggestion-loading">Loading...</div>
          ) : (
            <ul className="suggestions-list">
              {suggestions.map((item) => (
                <li key={item.id} className="suggestion-item">
                  {searchType === 'dishes' ? (
                    <Link to={`/dish/${item.id}`} className="suggestion-link">
                      <div className="suggestion-content">
                        <div className="suggestion-title">{item.title}</div>
                        <div className="suggestion-subtitle">{item.restaurantName}</div>
                        {item.tags && item.tags.length > 0 && (
                          <div className="suggestion-tags">
                            {item.tags.slice(0, 3).map((tag, index) => (
                              <span 
                                key={index} 
                                className={`suggestion-tag tag-${tag.toLowerCase().replace(/\s+/g, '-')}`}
                                style={getTagStyle(tag)}
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </Link>
                  ) : (
                    <Link to={`/user/${item.id}`} className="suggestion-link">
                      <div className="suggestion-content">
                        <div className="suggestion-title">{item.attributes?.username || 'Unknown User'}</div>
                        <div className="suggestion-subtitle">{item.attributes?.email || ''}</div>
                      </div>
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar; 